import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";

const graph = {
  foyer: {
    text: "The observatory is dark. A stopped clock reads 11:42.",
    choices: [
      { id: "study-clock", label: "Study the clock", next: "clock" },
      { id: "enter-library", label: "Enter the library", next: "library" },
    ],
  },
  clock: {
    text: "A silver fiber is caught behind the clock face.",
    choices: [
      { id: "take-fiber", label: "Collect the fiber", next: "library", clue: "silver-fiber" },
    ],
  },
  library: {
    text: "Three astronomy logs sit beside a cold cup of tea.",
    choices: [{ id: "read-log", label: "Read the newest log", next: "dome", clue: "altered-log" }],
  },
  dome: {
    text: "The telescope controls were used at 11:42 by a staff key.",
    choices: [{ id: "accuse", label: "Make an accusation", next: "solution" }],
  },
  solution: { text: "Choose who sabotaged the telescope.", choices: [] },
} as const;
type SceneId = keyof typeof graph;
export const mysteryStateSchema = z.object({
  players: z.array(z.string()),
  scene: z.enum(["foyer", "clock", "library", "dome", "solution"]),
  clues: z.array(z.string()),
  choices: z.array(z.string()),
  solutionId: z.string(),
  accusation: z.string().nullable(),
  phase: z.enum(["playing", "solved", "failed"]),
  checkpoint: z.number().int(),
});
export const mysteryEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("choose"), choiceId: z.string() }),
  z.object({ type: z.literal("accuse"), suspectId: z.string() }),
]);
export const mysterySettingsSchema = z.object({
  storyId: z.string().default("observatory-signal"),
});
type State = z.infer<typeof mysteryStateSchema>;
type Event = z.infer<typeof mysteryEventSchema>;
type Settings = z.infer<typeof mysterySettingsSchema>;
export const storyMysteryGame: TopzleGameDefinition<State, Event, Settings> = {
  id: "story-mystery",
  version: "1.0.0",
  name: "Story & Mystery Room",
  shortDescription: "Explore scenes, collect clues, and solve a fixed mystery together.",
  longDescription:
    "A deterministic story graph fixes the solution before play; AI may voice characters but cannot rewrite the truth.",
  category: "story",
  icon: "key",
  cover: "mystery",
  minPlayers: 1,
  maxPlayers: 8,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: true,
  supportsAiPlayers: false,
  supportsLocalAi: true,
  supportsCloudAi: true,
  supportsTournaments: false,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["split", "rail", "avatar_only", "game_fullscreen"],
  preferredLayout: "split",
  stateSchema: mysteryStateSchema,
  eventSchema: mysteryEventSchema,
  settingsSchema: mysterySettingsSchema,
  createInitialState(context) {
    return {
      players: context.playerIds,
      scene: "foyer",
      clues: [],
      choices: [],
      solutionId: "curator-vale",
      accusation: null,
      phase: "playing",
      checkpoint: 0,
    };
  },
  validateEvent(state, event) {
    if (state.phase !== "playing")
      return { valid: false, code: "finished", message: "The mystery is over" };
    if (event.type === "accuse")
      return {
        valid: state.scene === "solution",
        code: "not_ready",
        message: "Reach the solution scene first",
      };
    return {
      valid: graph[state.scene].choices.some((choice) => choice.id === event.choiceId),
      code: "locked_choice",
      message: "That choice is not available",
    };
  },
  reduceEvent(state, event) {
    if (event.type === "accuse")
      return {
        ...state,
        accusation: event.suspectId,
        phase: event.suspectId === state.solutionId ? "solved" : "failed",
      };
    const choice = graph[state.scene].choices.find((item) => item.id === event.choiceId);
    if (!choice) return state;
    const clue = "clue" in choice ? choice.clue : null;
    return {
      ...state,
      scene: choice.next as SceneId,
      choices: [...state.choices, choice.id],
      clues: clue && !state.clues.includes(clue) ? [...state.clues, clue] : state.clues,
      checkpoint: state.checkpoint + 1,
    };
  },
  getPublicState: (state) => ({ ...state, solutionId: undefined, sceneData: graph[state.scene] }),
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => ({
    ...state,
    solutionId: undefined,
    sceneData: graph[state.scene],
  }),
  getAvailableActions: (state) =>
    state.scene === "solution"
      ? [
          { type: "accuse", suspectId: "curator-vale" },
          { type: "accuse", suspectId: "engineer-rin" },
        ]
      : graph[state.scene].choices.map((choice) => ({ type: "choose", choiceId: choice.id })),
  calculateResult(state): GameResult | null {
    if (state.phase === "playing") return null;
    return {
      status: "completed",
      winnerIds: state.phase === "solved" ? state.players : [],
      scores: Object.fromEntries(state.players.map((id) => [id, state.phase === "solved" ? 1 : 0])),
      reason: state.phase === "solved" ? "mystery_solved" : "wrong_accusation",
    };
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase !== "playing" ||
      state.players.length >= 8 ||
      state.players.includes(arrival.playerId)
    )
      return null;
    return { state: { ...state, players: [...state.players, arrival.playerId] } };
  },
  handlePlayerDeparture(state, departure) {
    if (!state.players.includes(departure.playerId) || state.players.length <= 1) return null;
    return {
      state: {
        ...state,
        players: state.players.filter((id) => id !== departure.playerId),
      },
    };
  },
};
